// Size definitions
export const SIZES = {
  ADULT: ['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL'],
  ADULT_EXTENDED: ['4XL', '5XL', '6XL', '7XL'],
  YOUTH: ['YXS', 'YS', 'YM', 'YL', 'YXL'],
  TODDLER: ['2T', '3T', '4T', '5/6T'],
  // Infant: NewBorn + month ranges + legacy numeric-month codes
  INFANT: ['NewBorn', 'NB', '3-6', '6-12', '12-18', '18-24', '6M', '12M', '18M', '24M'],
  ONESIZE: ['OneSize']
};

export const SIZE_ORDER = [
  ...SIZES.INFANT,
  ...SIZES.TODDLER,
  ...SIZES.YOUTH,
  ...SIZES.ADULT,
  ...SIZES.ADULT_EXTENDED,
  ...SIZES.ONESIZE
];

// Status Enums
export const ORDER_STATUSES = {
  CREATED: 'Created',
  IN_PROGRESS: 'In Progress',
  READY_TO_ORDER: 'Ready To Order',
  ORDERED: 'Ordered',
  RECEIVED: 'Received',
  PARTIALLY_RECEIVED: 'Partially Received',
  PARTIALLY_READY_TO_PRINT: 'Partially Ready to Print',
  READY_TO_PRINT: 'Ready to Print',
  READY_TO_INVOICE: 'Ready To Invoice',
  WAITING_ON_REPRINT: 'Waiting on RePrint',
  COMPLETED: 'Completed',
  CANCELLED: 'Cancelled'
};

export const LINE_STATUSES = {
  NEW: 'New',
  READY_TO_ORDER: 'Ready To Order',
  ORDERED: 'Ordered',
  IN_NOT_CUT: 'IN not Cut',
  CUT_NOT_IN: 'CUT not IN',
  READY_TO_PRINT: 'Ready to Print',
  PRINTED: 'Printed',
  COMPLETED: 'Completed'
};

export const LINE_TYPES = {
  SCREEN_PRINT: 'Screen Print',
  EMBROIDERY: 'Embroidery',
  DTG: 'DTG',
  DTF: 'DTF',
  DESIGN_ONLY: 'Design Only'
};

// Keep LINE_ITEM_STATUSES as an alias for backward compatibility
export const LINE_ITEM_STATUSES = LINE_STATUSES;

export const PAYMENT_STATUSES = {
  NOT_DUE: 'not_due',
  AWAITING_PAYMENT: 'awaiting_payment',
  PENDING: 'pending',
  PAID: 'paid',
  PAID_OFFLINE: 'paid_offline',
  WAIVED: 'waived'
};

export const PAYMENT_STATUS_LABELS = {
  [PAYMENT_STATUSES.NOT_DUE]: 'Not due',
  [PAYMENT_STATUSES.AWAITING_PAYMENT]: 'Awaiting payment',
  [PAYMENT_STATUSES.PENDING]: 'Pending',
  [PAYMENT_STATUSES.PAID]: 'Paid',
  [PAYMENT_STATUSES.PAID_OFFLINE]: 'Paid (offline)',
  [PAYMENT_STATUSES.WAIVED]: 'Waived'
};

export const TERMINAL_PAYMENT_STATUSES = [
  PAYMENT_STATUSES.PAID,
  PAYMENT_STATUSES.PAID_OFFLINE,
  PAYMENT_STATUSES.WAIVED
];

/** AL-owned payment.breakdown.adjustments[].type — amounts are always positive. */
export const PAYMENT_ADJUSTMENT_TYPES = {
  TAXABLE_DISCOUNT: 'taxable_discount',
  NONTAXABLE_ADJUSTMENT: 'nontaxable_adjustment',
  DEPOSIT: 'deposit'
};

// Schema definitions
export const OrderSchema = {
  customerId: String,
  customerName: String,
  projectManager: String,
  startDate: Date,
  dueDate: Date,
  status: String,
  createdAt: Date,
  updatedAt: Date,
  updatedBy: String,
  notes: String,
  total: Number,
  totalCOGS: Number,
  archived: Boolean,  // Whether the order has been archived (typically true when status is COMPLETED)
  ordered: Boolean,
  readyToOrder: Boolean,
  facilityId: String  // 'central' | 'strawberryPatch'; missing ⇒ central
};

export const OrderLineSchema = {
  orderId: String,
  facilityId: String,  // Inherited from order; missing ⇒ central
  lineNumber: Number,
  type: String,
  status: String,
  designName: String,
  garmentId: String,
  garmentColor: String,
  printLocations: Array, // ['Front', 'Back', 'Other']
  sizes: Object,
  totalQuantity: Number,
  mockupUrl: String,
  designFileUrl: String,
  createdAt: Date,
  updatedAt: Date,
  updatedBy: String,
  pricePerPiece: Number,
  totalLinePrice: Number,
  totalLineCOGS: Number,
  isIn: Boolean,
  isCut: Boolean,
  printed: Boolean,
  needsReorder: Boolean,
  // Ink color fields
  frontInkColors: Array,  // Array of color names for front print
  backInkColors: Array,   // Array of color names for back print
  otherInkColors: Array,   // Array of color names for other locations
  // AutoPress setup per print location (front/back/other)
  pressSetup: Object,
  // DTG specific fields
  dtgArtworkReady: Boolean,  // Indicates if print-ready artwork has been uploaded for DTG jobs
  dtgArtworkUrl: String,      // URL to the print-ready DTG artwork file
  // Location-specific artwork URLs for DTG
  frontArtworkUrl: String,    // URL to front location artwork for DTG
  backArtworkUrl: String,     // URL to back location artwork for DTG
  otherArtworkUrl: String     // URL to other location artwork for DTG
};

// Helper functions
export const getSizeType = (size) => {
  if (SIZES.ADULT.includes(size)) return 'ADULT';
  if (SIZES.ADULT_EXTENDED.includes(size)) return 'ADULT_EXTENDED';
  if (SIZES.YOUTH.includes(size)) return 'YOUTH';
  if (SIZES.TODDLER.includes(size)) return 'TODDLER';
  if (SIZES.INFANT.includes(size)) return 'INFANT';
  if (SIZES.ONESIZE.includes(size)) return 'ONESIZE';
  return 'UNKNOWN';
};

export const sortSizes = (sizeA, sizeB) => {
  const indexA = SIZE_ORDER.indexOf(sizeA);
  const indexB = SIZE_ORDER.indexOf(sizeB);
  if (indexA === -1) return 1;
  if (indexB === -1) return -1;
  return indexA - indexB;
};

export const FEE_TYPES = {
  ARTWORK: 'Artwork Fee',
  SETUP: 'Setup Fee',
  EXTRA_SCREEN: 'Extra Screen',
  RUSH: 'Rush Fee',
  FLASH: 'Flash Fee',
  SHIPPING: 'Shipping',
  DOUBLE_SIDED: 'Double Sided',
  DESIGN: 'Design',
  INK_CHANGE: 'Ink Change',
  DELIVERY: 'Delivery',
  DISCOUNT: 'Discount'
};

export const canUpdateLineStatus = (currentStatus, newStatus) => {
  // Define valid status transitions
  const validTransitions = {
    [LINE_STATUSES.NEW]: [
      LINE_STATUSES.READY_TO_ORDER,
      LINE_STATUSES.ORDERED,
      LINE_STATUSES.IN_NOT_CUT,
      LINE_STATUSES.CUT_NOT_IN,
      LINE_STATUSES.READY_TO_PRINT
    ],
    [LINE_STATUSES.READY_TO_ORDER]: [
      LINE_STATUSES.ORDERED,
      LINE_STATUSES.NEW
    ],
    [LINE_STATUSES.ORDERED]: [
      LINE_STATUSES.IN_NOT_CUT,
      LINE_STATUSES.CUT_NOT_IN,
      LINE_STATUSES.READY_TO_PRINT
    ],
    [LINE_STATUSES.IN_NOT_CUT]: [
      LINE_STATUSES.READY_TO_PRINT
    ],
    [LINE_STATUSES.CUT_NOT_IN]: [
      LINE_STATUSES.READY_TO_PRINT
    ],
    [LINE_STATUSES.READY_TO_PRINT]: [
      LINE_STATUSES.PRINTED,
      LINE_STATUSES.COMPLETED
    ],
    [LINE_STATUSES.PRINTED]: [
      LINE_STATUSES.COMPLETED
    ],
    [LINE_STATUSES.COMPLETED]: [] // No transitions from completed
  };

  // Allow transition to same status
  if (currentStatus === newStatus) return true;

  // Check if transition is valid
  return validTransitions[currentStatus]?.includes(newStatus) || false;
};

/** Display label for order status; shows "Provided Goods" when order is ORDERED and providedGoods is set */
export const getOrderStatusDisplay = (order) => {
  if (!order) return '';
  if (order.providedGoods && order.status === ORDER_STATUSES.ORDERED) return 'Provided Goods';
  return order.status || '';
};

/** Display label for line status when order has providedGoods or sample; pass order or flags on line */
export const getLineStatusDisplay = (line, orderOrFlags) => {
  const order = orderOrFlags && typeof orderOrFlags === 'object' ? orderOrFlags : null;
  const providedGoods = order
    ? order.providedGoods
    : orderOrFlags;
  const sample = order
    ? (order.sample === true || order.additionalInstructions?.sample === true)
    : (line?.orderSample === true);
  if (sample && line?.status === LINE_STATUSES.COMPLETED) return 'Sample (Ready)';
  if (sample && line?.isIn) return 'Sample (In)';
  if (line?.status === LINE_STATUSES.ORDERED && providedGoods) return 'Provided Goods';
  return line?.status || '';
};

// Document Types
export const DOC_TYPES = {
  QUOTE: 'Quote',
  ORDER: 'Order'
};

// Quote Statuses
export const QUOTE_STATUSES = {
  DRAFT: 'Draft',
  SENT: 'Sent',
  PENDING_APPROVAL: 'Pending Approval',
  APPROVED: 'Approved',
  CONVERTED: 'Converted to Order',
  EXPIRED: 'Expired',
  DECLINED: 'Declined'
};

// Quote Schema
export const QuoteSchema = {
  ...OrderSchema,  // Inherit base fields from OrderSchema
  type: String,    // Will be DOC_TYPES.QUOTE
  status: String,  // One of QUOTE_STATUSES
  expiresAt: Date,
  convertedOrderId: String,
  followUpDate: Date,
  probability: Number,
  revisionNumber: Number,
  quoteNumber: String,  // For reference (e.g., "Q2024-001")
  validUntil: Date,
  approvedBy: String,
  approvedAt: Date,
  convertedAt: Date,
  convertedBy: String
}; 